import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import styles from "./ContactBar.module.css";

interface ContactBarProps {
  imageSrc?: string;
  wordButton?: string;
}

const MOBILE_DRAWER_CLOSE_MS = 320;

const NAV_ITEMS = [
  { id: "CardId", label: "О нас" },
  { id: "TestimonialsId", label: "Отзывы" },
  { id: "MapId", label: "Контакты" },
  { id: "FAQ", label: "Вопросы" },
  { id: "Service", label: "Услуги" },
  { id: "Projects", label: "Проекты" },
  { id: "Promotions", label: "Акции" },
] as const;

type SectionMap = Record<(typeof NAV_ITEMS)[number]["id"], boolean>;

const INITIAL_SECTIONS: SectionMap = {
  CardId: false,
  TestimonialsId: false,
  MapId: false,
  FAQ: false,
  Service: false,
  Projects: false,
  Promotions: false,
};

const ContactBar: React.FC<ContactBarProps> = ({
  imageSrc = "/logo.png",
  wordButton = "Подать заявку",
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileMenuMounted, setMobileMenuMounted] = useState(false);
  const [sections, setSections] = useState<SectionMap>(INITIAL_SECTIONS);

  const checkSections = useCallback(() => {
    setSections({
      CardId: !!document.getElementById("CardId"),
      TestimonialsId: !!document.getElementById("TestimonialsId"),
      MapId: !!document.getElementById("MapId"),
      FAQ: !!document.getElementById("FAQ"),
      Service: !!document.getElementById("Service"),
      Projects: !!document.getElementById("Projects"),
      Promotions: !!document.getElementById("Promotions"),
    });
  }, []);

  useEffect(() => {
    checkSections();

    const observer = new MutationObserver(checkSections);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [checkSections]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      setMobileMenuMounted(true);
      return;
    }

    if (!mobileMenuMounted) {
      return;
    }

    const closeTimer = window.setTimeout(() => {
      setMobileMenuMounted(false);
    }, MOBILE_DRAWER_CLOSE_MS);

    return () => window.clearTimeout(closeTimer);
  }, [mobileMenuMounted, mobileMenuOpen]);

  useEffect(() => {
    if (!mobileMenuOpen) {
      document.body.style.overflow = "";
      return;
    }

    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMobileMenuOpen(false);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEsc);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleEsc);
    };
  }, [mobileMenuOpen]);

  const visibleNavItems = useMemo(
    () => NAV_ITEMS.filter((item) => sections[item.id]),
    [sections]
  );

  const scrollToSection = useCallback((id: string) => {
    const element = document.getElementById(id);
    if (!element) {
      return;
    }

    element.scrollIntoView({ behavior: "smooth", block: "start" });
    setMobileMenuOpen(false);
  }, []);

  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen((prev) => !prev);
  }, []);

  const renderNavItems = useCallback(
    (isMobile: boolean) =>
      visibleNavItems.map((item, index) => (
        <button
          key={`${isMobile ? "mobile" : "desktop"}-${item.id}`}
          onClick={() => scrollToSection(item.id)}
          className={styles.navLink}
          style={
            isMobile
              ? ({ "--nav-item-delay": `${index * 55}ms` } as CSSProperties)
              : undefined
          }
          type="button"
        >
          {item.label}
        </button>
      )),
    [scrollToSection, visibleNavItems]
  );

  return (
    <>
      <header className={`${styles.contactBar} ${isScrolled ? styles.scrolled : ""}`}>
        <button
          className={`${styles.hamburger} ${mobileMenuOpen ? styles.hamburgerActive : ""}`}
          onClick={toggleMobileMenu}
          aria-label={mobileMenuOpen ? "Закрыть меню" : "Открыть меню"}
          aria-expanded={mobileMenuOpen}
          type="button"
        >
          <span />
          <span />
          <span />
        </button>

        <div className={styles.logoContainer}>
          <img
            src={imageSrc}
            alt="Логотип компании"
            className={styles.logo}
            loading="lazy"
          />
        </div>

        <nav className={`${styles.nav} ${styles.desktopNav}`} aria-label="Основное меню">
          {renderNavItems(false)}
        </nav>

        <div className={styles.contactGroup}>
          <button
            onClick={() => scrollToSection("ContactFormCardId")}
            className={styles.applyButton}
            type="button"
          >
            {wordButton}
          </button>
        </div>
      </header>

      {mobileMenuMounted && (
        <div
          className={`${styles.mobileDrawer} ${mobileMenuOpen ? styles.mobileDrawerOpen : styles.mobileDrawerClosing}`}
        >
          <div
            className={`${styles.mobileOverlay} ${mobileMenuOpen ? styles.mobileOverlayOpen : styles.mobileOverlayClosing}`}
            onClick={toggleMobileMenu}
            aria-hidden="true"
          />

          <nav
            className={`${styles.mobilePanel} ${mobileMenuOpen ? styles.mobilePanelOpen : styles.mobilePanelClosing}`}
            aria-label="Мобильное меню"
          >
            <div className={styles.mobilePanelInner}>{renderNavItems(true)}</div>
          </nav>
        </div>
      )}
    </>
  );
};

export default ContactBar;
